import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import {
  loadCart,
  saveCart,
  updateProductStock,
  saveOrder,
  getOrderCount,
  updateOrderCount,
} from '../services/firestoreService';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { getDoc, doc, updateDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import Modal from 'react-modal';
import styles from './Checkout.module.css';
import provinces from '../data/provinces.json'; // Import provinces from JSON
import cantonPrices from '../data/cantonPrices.json'; // Import canton prices from JSON
import Swal from 'sweetalert2';

// ——— lightweight image helpers for the order summary ———
const toWebpVariant = (url, w = 1200) => {
  if (!url || typeof url !== "string") return url;
  // Cloudinary → return a tiny, auto-formatted thumb
  if (url.includes("res.cloudinary.com") && url.includes("/upload/")) {
    return url.replace("/upload/", "/upload/f_auto,q_auto:eco,c_fill,g_auto,w_160,h_160/");
  }
  if (/\.webp(\?|$)/i.test(url)) return url; // already webp
  const [base, qs = ""] = url.split("?");
  const out = base.replace(/(\.[^.?#]+)$/, `-w${w}.webp`);
  return qs ? `${out}?${qs}` : out;
};

const getOrderThumb = (item) =>
  item?.cartImageUrl ||
  toWebpVariant(item?.principalImageUrl, 1200) ||
  "/images/placeholder-96.png";


const Checkout = ({ toggleCartSidebar }) => {
  const { cart, setCart } = useCart();
  const [recipientEmail, setRecipientEmail] = useState(auth.currentUser ? auth.currentUser.email : '');
  const [shippingMethod, setShippingMethod] = useState('delivery');
  const [contactInfo, setContactInfo] = useState({
    recipientName: '',
    recipientLastName: '',
    telefono: '',
  });
  const [shippingInfo, setShippingInfo] = useState({
    direccion: '',
    provincia: '',
    canton: '',
    distrito: '',
    codigoPostal: '',
    pais: 'Costa Rica',
  });
  const [deliveryPrice, setDeliveryPrice] = useState(0); // Initial delivery price
  const [saveAccountInfo, setSaveAccountInfo] = useState(false);
  const [saveDeliveryInfo, setSaveDeliveryInfo] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isPaymentProcessed, setIsPaymentProcessed] = useState(false);
  const [orderID, setOrderID] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false); // New state for disabling button
  const [deliveryDate, setDeliveryDate] = useState(''); // For date input
  const [deliveryRange, setDeliveryRange] = useState(''); // For delivery range input
  const [additionalComments, setAdditionalComments] = useState(''); // Optional comments
  const navigate = useNavigate();

    // --- Discount code state ---
  const [discountCode, setDiscountCode] = useState('');
  const [discountData, setDiscountData] = useState(null); // {code,type,value,isTemporal,...}
  const [discountMessage, setDiscountMessage] = useState('');
  const [isCheckingDiscount, setIsCheckingDiscount] = useState(false);


  // --- PayPal fee config/state ---
const PAYPAL_FEE_USD = 3;
const [paypalFeeCRC, setPaypalFeeCRC] = useState(0);
const [fxLoading, setFxLoading] = useState(false);
const [fxError, setFxError] = useState(null);

// 👉 Replace with your real Cloud Function URL/region
// Example if deployed in us-central1:
// BCCR Cloud Function (deployed name shown in your console: usdCrcRate)
const BCCR_FX_URL =
  "https://usdcrcrate-6oigkk6uoq-uc.a.run.app?which=venta";



  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const namePattern = /^[a-zA-Z\s]+$/;
  const phonePattern = /^[0-9]{8}$/; // Costa Rican phone number (8 digits)
  const postalCodePattern = /^[0-9]+$/;

  const [quienRecibe, setQuienRecibe] = useState({
    name: '',
    phone: '',
  });
  const [isSameAsContact, setIsSameAsContact] = useState(false);
  
  const handleQuienRecibeChange = (e) => {
    const { name, value } = e.target;
    setQuienRecibe((prevInfo) => ({ ...prevInfo, [name]: value }));
  };
  
  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setIsSameAsContact(isChecked);
  
    if (isChecked) {
      setQuienRecibe({
        name: contactInfo.recipientName,
        phone: contactInfo.telefono,
      });
    } else {
      setQuienRecibe({
        name: '',
        phone: '',
      });
    }
  };

  async function fetchPaypalFeeFromBccr() {
    try {
      setFxLoading(true);
      setFxError(null);
  
      const res = await fetch(BCCR_FX_URL, { cache: "no-store" });
      const data = await res.json();
  
      if (!res.ok || !data?.ok || !Number.isFinite(data.rate)) {
        throw new Error("BCCR function error");
      }
  
      // US$3 converted to CRC using the BCCR rate
      const feeCRC = Math.round(data.rate * PAYPAL_FEE_USD);
      setPaypalFeeCRC(feeCRC);
    } catch (e) {
      console.error(e);
      setPaypalFeeCRC(0);
      setFxError("No se pudo obtener la tasa del BCCR.");
    } finally {
      setFxLoading(false);
    }
  }
  
  
  

  useEffect(() => {
    const fetchUserData = async () => {
      if (auth.currentUser) {
        const userDoc = await getDoc(doc(firestore, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setContactInfo({
            recipientName: data.name || '',
            recipientLastName: data.lastName || '',
            telefono: data.telefono || '',
          });
          setShippingInfo({
            direccion: data.direccion || '',
            provincia: data.provincia || 'San José',
            canton: data.canton || '',
            distrito: data.distrito || '',
            codigoPostal: data.codigoPostal || '',
            pais: 'Costa Rica',
          });
          setRecipientEmail(auth.currentUser.email);
        }
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (shippingMethod === 'pickup') {
      setDeliveryPrice(0);
    } else {
      const price = cantonPrices[shippingInfo.canton] || 0;
      setDeliveryPrice(price);
    }
  }, [shippingInfo.canton, shippingMethod]);


  // Fetch PayPal fee when PayPal is selected
useEffect(() => {
  if (paymentMethod === "PayPal") {
    fetchPaypalFeeFromBccr();
  } else {
    setPaypalFeeCRC(0);
    setFxError(null);
    setFxLoading(false);
  }
}, [paymentMethod]);


  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactInfo((prevInfo) => ({ ...prevInfo, [name]: value }));
  };

  const handleShippingInputChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo((prevInfo) => ({ ...prevInfo, [name]: value }));

    if (name === 'provincia') {
      setShippingInfo((prevInfo) => ({ ...prevInfo, canton: '', distrito: '' }));
    }
  };

  const handleEmailChange = (e) => {
    setRecipientEmail(e.target.value);
  };

  const validateFields = () => {
    const today = new Date().toISOString().split('T')[0];
  
    if (!deliveryRange) {
      Swal.fire('Error', 'Por favor, selecciona un rango de hora para la entrega.', 'error');
      return false;
    }
    if (!emailPattern.test(recipientEmail)) {
      Swal.fire('Error', 'Por favor, ingresa un correo electrónico válido.', 'error');
      return false;
    }
    if (!namePattern.test(contactInfo.recipientName)) {
      Swal.fire('Error', 'Por favor, ingresa un nombre válido.', 'error');
      return false;
    }
    if (!namePattern.test(contactInfo.recipientLastName)) {
      Swal.fire('Error', 'Por favor, ingresa apellidos válidos.', 'error');
      return false;
    }
    if (!phonePattern.test(contactInfo.telefono)) {
      Swal.fire('Error', 'Por favor, ingresa un número de teléfono válido (8 dígitos).', 'error');
      return false;
    }
    if (shippingMethod === 'delivery') {
      if (!shippingInfo.provincia) {
        Swal.fire('Error', 'Por favor, selecciona una provincia válida.', 'error');
        return false;
      }
      if (!shippingInfo.canton) {
        Swal.fire('Error', 'Por favor, selecciona un cantón válido.', 'error');
        return false;
      }
      if (!shippingInfo.distrito) {
        Swal.fire('Error', 'Por favor, selecciona un distrito válido.', 'error');
        return false;
      }
      if (!shippingInfo.direccion) {
        Swal.fire('Error', 'Por favor, ingresa una dirección válida.', 'error');
        return false;
      }
    }
    if (!deliveryDate || deliveryDate < today) {
      Swal.fire('Error', 'Por favor, selecciona una fecha de entrega válida.', 'error');
      return false;
    }
    if (!paymentMethod) {
      Swal.fire('Error', 'Por favor, selecciona un método de pago.', 'error');
      return false;
    }
  
    // Add validation for "Quien Recibe" information
    if (!isSameAsContact) {
      if (!namePattern.test(quienRecibe.name)) {
        Swal.fire('Error', 'Por favor, ingresa un nombre válido para quien recibe.', 'error');
        return false;
      }
      if (!phonePattern.test(quienRecibe.phone)) {
        Swal.fire('Error', 'Por favor, ingresa un número de teléfono válido para quien recibe.', 'error');
        return false;
      }
    }
  
    return true; // Return true if all validations pass
  };

    const normalizeDiscountCode = (code) =>
    code ? code.trim().toUpperCase() : '';

  const computeDiscountAmount = (baseAmount, discount) => {
    if (!discount) return 0;
    const value = Number(discount.value) || 0;
    if (value <= 0) return 0;

    let amount = 0;
    if (discount.type === 'percent') {
      amount = Math.round(baseAmount * (value / 100));
    } else if (discount.type === 'fixed') {
      amount = value;
    }
    if (amount > baseAmount) amount = baseAmount;
    return amount;
  };

  // Valida en Firestore y opcionalmente muestra alertas / actualiza estado
  const validateDiscountCodeInFirestore = async (
    rawCode,
    { showAlerts = false, updateState = true } = {}
  ) => {
    const code = normalizeDiscountCode(rawCode);
    if (!code) {
      if (updateState) {
        setDiscountData(null);
        setDiscountMessage('');
      }
      return { ok: false, reason: 'EMPTY' };
    }

    try {
      setIsCheckingDiscount(true);
      const ref = doc(firestore, 'discountCodes', code);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        const msg = 'El código ingresado no existe.';
        if (updateState) {
          setDiscountData(null);
          setDiscountMessage(msg);
        }
        if (showAlerts) {
          Swal.fire('Código inválido', msg, 'error');
        }
        return { ok: false, reason: 'NOT_FOUND' };
      }

      const data = snap.data();
      const today = new Date().toISOString().split('T')[0];

      // Deshabilitado
      if (!data.isEnabled) {
        const msg = 'Este código está deshabilitado.';
        if (updateState) {
          setDiscountData(null);
          setDiscountMessage(msg);
        }
        if (showAlerts) {
          Swal.fire('Código deshabilitado', msg, 'error');
        }
        return { ok: false, reason: 'DISABLED' };
      }

      // Temporalidad
      if (data.isTemporal) {
        if (data.startDate && today < data.startDate) {
          const msg = 'Este código aún no está activo.';
          if (updateState) {
            setDiscountData(null);
            setDiscountMessage(msg);
          }
          if (showAlerts) {
            Swal.fire('Código no activo', msg, 'error');
          }
          return { ok: false, reason: 'NOT_STARTED' };
        }

        if (data.endDate && today > data.endDate) {
          const msg = 'Este código ya expiró.';
          // Auto-desactivar al expirar
          try {
            await updateDoc(ref, { isEnabled: false });
          } catch (e) {
            console.warn('No se pudo auto-desactivar el código expirado:', e);
          }

          if (updateState) {
            setDiscountData(null);
            setDiscountMessage(msg);
          }
          if (showAlerts) {
            Swal.fire('Código expirado', msg, 'error');
          }
          return { ok: false, reason: 'EXPIRED' };
        }
      }

      const normalizedData = {
        code: data.code || code,
        type: data.type || 'percent',
        value: data.value || 0,
        isTemporal: !!data.isTemporal,
        startDate: data.startDate || '',
        endDate: data.endDate || '',
      };

      if (updateState) {
        setDiscountData(normalizedData);
        setDiscountMessage('Código aplicado correctamente ✔️');
      }

      return { ok: true, data: normalizedData };
    } catch (err) {
      console.error('Error validando código de descuento:', err);
      const msg = 'No se pudo validar el código. Intenta de nuevo.';
      if (updateState) {
        setDiscountMessage(msg);
      }
      if (showAlerts) {
        Swal.fire('Error', msg, 'error');
      }
      return { ok: false, reason: 'ERROR', error: err };
    } finally {
      setIsCheckingDiscount(false);
    }
  };

  const handleDiscountCodeChange = (e) => {
    const value = e.target.value;
    setDiscountCode(value);
    if (!value.trim()) {
      setDiscountData(null);
      setDiscountMessage('');
    }
  };

  const handleApplyDiscount = async () => {
    await validateDiscountCodeInFirestore(discountCode, {
      showAlerts: false,
      updateState: true,
    });
  };

  
const handleCheckout = async () => {
  try {
    // 1. Leer SIEMPRE el estado más reciente de la tienda antes de procesar
    const settingsRef = doc(firestore, 'businessSettings', 'main');
    const settingsSnap = await getDoc(settingsRef);

    if (settingsSnap.exists()) {
      const settingsData = settingsSnap.data();

      // Si isOpen es false (o no es true), BLOQUEAMOS el pedido
      if (!settingsData.isOpen) {
        Swal.fire(
          'Tienda cerrada',
          'En este momento la tienda está cerrada y no podemos procesar pedidos. ' +
            'Puedes revisar tu carrito, pero deberás volver cuando la tienda esté abierta.',
          'warning'
        );
        return;
      }
    } else {
      console.warn('⚠️ No se encontró businessSettings/main, se asume tienda abierta.');
    }

    // 2. Validar campos SOLO si la tienda está abierta
    if (!validateFields()) return;
        // 2.1. Revalidar código de descuento si el cliente ingresó uno
    let discountForCheckout = null;
    if (discountCode.trim()) {
      const { ok, data } = await validateDiscountCodeInFirestore(discountCode, {
        showAlerts: true,
        updateState: true,
      });

      // Si el código es inválido, NO continuamos con el checkout
      if (!ok) {
        return;
      }
      discountForCheckout = data;
    }


    setIsProcessing(true);

    let isStockAvailable = true;
    const updatedCart = [];

    // 🔽 A partir de aquí DEJAS TODO IGUAL que ya tienes
    // (check de stock, creación del order, saveOrder, updateOrderCount, etc.)


        // Check stock availability for each item
        for (const item of cart) {
            const newStock = await updateProductStock(item.id, item.quantity);
            if (newStock < 0) {
                isStockAvailable = false;
                updatedCart.push({ ...item, outOfStock: true });
            } else {
                updatedCart.push({ ...item, outOfStock: false });
            }
        }

        if (isStockAvailable) {
          const currentOrderCount = await getOrderCount();
          const newOrderNumber = Number.isFinite(currentOrderCount) ? currentOrderCount + 1 : 1;

          // Subtotal basado en el carrito actualizado (ignorando items agotados)
          const cartSubtotal = updatedCart.reduce(
            (acc, item) =>
              item.outOfStock ? acc : acc + item.price * item.quantity,
            0
          );

          const baseForDiscount = cartSubtotal + deliveryPrice;

          // Calcular descuento real al momento de crear la orden
          let discountAmountToSave = 0;
          let discountInfoToSave = null;

          if (discountForCheckout) {
            discountAmountToSave = computeDiscountAmount(
              baseForDiscount,
              discountForCheckout
            );

            if (discountAmountToSave > 0) {
              discountInfoToSave = {
                code: discountForCheckout.code,
                type: discountForCheckout.type,
                value: Number(discountForCheckout.value) || 0,
                amount: discountAmountToSave,
              };
            }
          }

          const paypalFeeToUse = paymentMethod === 'PayPal' ? paypalFeeCRC : 0;

          const totalBeforePaypal = baseForDiscount - discountAmountToSave;
          const finalTotal = totalBeforePaypal + paypalFeeToUse;

          const order = {
            userId: auth.currentUser ? auth.currentUser.uid : 'guest',
            email: recipientEmail,
            items: updatedCart,
            total: finalTotal,
            shippingMethod,
            contactInfo,
            quienRecibe,
            shippingInfo: shippingMethod === 'delivery' ? shippingInfo : null,
            deliveryPrice,
            paymentMethod,
            date: new Date().toISOString(),
            orderNumber: newOrderNumber,
            deliveryDate: deliveryDate || null,
            deliveryRange: deliveryRange || null,
            additionalComments: additionalComments || null,
            // almacenar comisión PayPal
            paypalFeeCRC: paypalFeeToUse,
            // info del descuento aplicado (si existió)
            discount: discountInfoToSave,
          };


            // Save the order to Firestore
            await saveOrder(order);
            await updateOrderCount(newOrderNumber);

            // Update user's order history and spending if logged in
              if (auth.currentUser) {
                const userDocRef = doc(firestore, 'users', auth.currentUser.uid);
                const snap = await getDoc(userDocRef);
                const userData = snap.exists() ? snap.data() : {};

                await setDoc(
                  userDocRef,
                  {
                    ordersPlaced: (userData.ordersPlaced || 0) + 1,
                    totalSpent: (userData.totalSpent || 0) + order.total,
                  },
                  { merge: true }
                );
              }


            // Save account info if the option is checked
              if (auth.currentUser && saveAccountInfo) {
                const userDocRef = doc(firestore, 'users', auth.currentUser.uid);
                await setDoc(
                  userDocRef,
                  {
                    name: contactInfo.recipientName,
                    lastName: contactInfo.recipientLastName,
                    telefono: contactInfo.telefono,
                  },
                  { merge: true }
                );
              }

            // Save delivery info if the option is checked
              if (auth.currentUser && saveDeliveryInfo) {
                const userDocRef = doc(firestore, 'users', auth.currentUser.uid);
                await setDoc(
                  userDocRef,
                  {
                    direccion: shippingInfo.direccion,
                    provincia: shippingInfo.provincia,
                    canton: shippingInfo.canton,
                    distrito: shippingInfo.distrito,
                    codigoPostal: shippingInfo.codigoPostal,
                  },
                  { merge: true }
                );
              }


            // Clear cart and local storage
            setCart([]);
            localStorage.removeItem('cart');
            if (auth.currentUser) {
                await saveCart([]);
            }

            // Set the order ID and indicate payment processed
            setOrderID(newOrderNumber);
            setIsPaymentProcessed(true);
        } else {
            Swal.fire('Error', 'Algunos artículos están agotados. Revisa tu carrito.', 'error');
        }
        } catch (err) {
          console.error('Checkout error:', err);
          const code = (err && err.code) ? ` (${err.code})` : '';
          Swal.fire('Error', `Hubo un problema al procesar tu pedido${code}. Intenta de nuevo.`, 'error');
        } finally {

        setIsProcessing(false);
    }
};


  const handleCloseModal = async () => {
    setIsPaymentProcessed(false);
    navigate('/order-history');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // --- Totals for summary ---
const cartSubtotal = cart.reduce(
  (acc, item) => acc + item.price * item.quantity,
  0
);
const paypalFeeToUse = paymentMethod === "PayPal" ? paypalFeeCRC : 0;

// Base para descuento: productos + envío (no incluye comisión PayPal)
const discountBase = cartSubtotal + deliveryPrice;
const discountAmount = computeDiscountAmount(discountBase, discountData);

const totalBeforePaypal = discountBase - discountAmount;
const orderTotal = totalBeforePaypal + paypalFeeToUse;



  return (
    <div className={styles.checkoutContainer}>
      <h2 className={styles.title}>Checkout</h2>

      <div className={`${styles.formGroup} ${styles.contactInfo}`}>
        <h3 className={styles.sectionTitle}>Información de Contacto</h3>

        <div className={styles.inputContainer}>
          <i className={`fa fa-envelope ${styles.icon}`}></i>
          <input
            type="email"
            value={recipientEmail}
            onChange={handleEmailChange}
            placeholder="Email"
            required
            className={styles.formControl}
          />
        </div>

        <div className={styles.inputContainer}>
          <i className={`fa fa-user ${styles.icon}`}></i>
          <input
            type="text"
            name="recipientName"
            value={contactInfo.recipientName}
            onChange={handleContactInputChange}
            placeholder="Nombre"
            required
            className={styles.formControl}
          />
        </div>
        <div className={styles.inputContainer}>
          <i className={`fa fa-user ${styles.icon}`}></i>
          <input
            type="text"
            name="recipientLastName"
            value={contactInfo.recipientLastName}
            onChange={handleContactInputChange}
            placeholder="Apellidos"
            required
            className={styles.formControl}
          />
        </div>
        <div className={styles.inputContainer}>
          <i className={`fa fa-phone ${styles.icon}`}></i>
          <input
            type="text"
            name="telefono"
            value={contactInfo.telefono}
            onChange={handleContactInputChange}
            placeholder="Teléfono"
            required
            className={styles.formControl}
          />
        </div>
      </div>

      {auth.currentUser && (
        <div className={styles.checkboxGroup}>
          <input
            type="checkbox"
            className="form-check-input"
            id="saveAccountInfo"
            checked={saveAccountInfo}
            onChange={(e) => setSaveAccountInfo(e.target.checked)}
          />
          <label htmlFor="saveAccountInfo">Guardar información de contacto para futuras compras</label>
        </div>
      )}

      <div className={`${styles.formGroup} ${styles.deliverySection}`}>
        <h4 className={styles.sectionTitle}>Día de Entrega</h4>
        <input
          type="date"
          name="deliveryDate"
          value={deliveryDate}
          onChange={(e) => setDeliveryDate(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
          className={`${styles.formControl} ${styles.shortInput}`}
          required
        />
      </div>

      <div className={`${styles.formGroup} ${styles.deliverySection}`}>
        <h4 className={styles.sectionTitle}>Hora de Entrega</h4>
        <select
          name="deliveryRange"
          value={deliveryRange}
          onChange={(e) => setDeliveryRange(e.target.value)}
          className={styles.formControl}
          required
        >
          <option value="" disabled>
            Selecciona un rango
          </option>
          <option value="6 a 9 AM">6 a 9 AM</option>
          <option value="9 a 11 AM">9 a 11 AM</option>
          <option value="11 AM a 1 PM">11 AM a 1 PM</option>
          <option value="1 a 3 PM">1 a 3 PM</option>
          <option value="3 a 6 PM">3 a 6 PM</option>
          <option value="6 a 9 PM">6 a 9 PM</option>
          <option value="9 a 11 PM">9 a 11 PM</option>
        </select>
      </div>

      <div className={styles.formGroup}>
        <h4 className={styles.sectionTitle}>Comentarios Adicionales (Opcional)</h4>
        <textarea
          name="additionalComments"
          value={additionalComments}
          onChange={(e) => setAdditionalComments(e.target.value)}
          placeholder="Especifica instrucciones especiales para tu pedido o entrega"
          className={styles.formControl}
        />
      </div>

      <div className={`${styles.formGroup} ${styles.shippingSection}`}>
        <h3 className={styles.sectionTitle}>Método de Entrega</h3>
        <div className={styles.radioGroup}>
          <label>
            <input
              type="radio"
              name="shippingMethod"
              value="pickup"
              checked={shippingMethod === 'pickup'}
              onChange={(e) => setShippingMethod(e.target.value)}
            />
            Recoger en el Local
          </label>
          <label>
            <input
              type="radio"
              name="shippingMethod"
              value="delivery"
              checked={shippingMethod === 'delivery'}
              onChange={(e) => setShippingMethod(e.target.value)}
            />
            Envío
          </label>

          <label>
            <input
              type="radio"
              name="shippingMethod"
              value="courier_pickup"
              checked={shippingMethod === 'courier_pickup'}
              onChange={(e) => setShippingMethod(e.target.value)}
            />
            Mandar a recoger (Uber, Uber Flash, mensajero, etc.)
          </label>

        </div>
      </div>


      {shippingMethod === 'courier_pickup' && (
  <div className={styles.pickupInfo}>
    <p><strong>Entrega a repartidor del cliente</strong></p>

    <p><strong>Estamos en:</strong> Plaza Vistana Oeste</p>
    <p><strong>Dirección:</strong> Lindora, frente a TacoBell, San José, Pozos.</p>

    <p>
      Preparamos y <strong>embalamos cada pedido con el mayor cuidado</strong>. 
      Si eliges mandar a recoger tu pedido con Uber, Uber Flash o un mensajero propio, 
      te avisaremos cuando esté listo y cuando <strong>lo entreguemos al repartidor</strong> que indiques.
    </p>

    <p>
      <strong>Importante:</strong> La <em>garantía de transporte</em> por daños durante el traslado 
      aplica únicamente para la opción <strong>“Envío”</strong> realizada directamente por Armony. 
      En la opción <strong>“Mandar a recoger”</strong>, nuestra responsabilidad 
      se limita hasta el momento de entrega al repartidor (podemos registrar evidencia de entrega si lo requieres).
    </p>

    <p style={{ fontSize: '0.95rem' }}>
      <strong>Consejos rápidos:</strong> pide un vehículo con aire acondicionado, solicita un viaje directo y 
      evita horas de alto calor para mantener el arreglo en óptimas condiciones.
    </p>
  </div>
)}


      {shippingMethod === 'pickup' && (
        <div className={styles.pickupInfo}>
          <p>
            <strong>Estamos en:</strong> Plaza Vistana Oeste
          </p>
          <p>
            <strong>Dirección:</strong> Lindora, frente a TacoBell, San José Pozos
          </p>
        </div>
      )}

{shippingMethod === 'delivery' && (
  <div className={styles.formGroup}>
    <h4 className={styles.sectionTitle}>Información de Entrega</h4>
     <textarea
      type="text"
      name="direccion"
      value={shippingInfo.direccion}
      onChange={handleShippingInputChange}
      placeholder="Dirección Completa"
      required
      className={styles.formControl}
    />
    <div className={styles.addressFields}>
      <label>Provincia</label>
      <select
        name="provincia"
        value={shippingInfo.provincia}
        onChange={handleShippingInputChange}
        className={styles.formControl}
        required
      >
        <option value="" disabled>Seleccionar una opción</option>
        {Object.keys(provinces).map((prov) => (
          <option key={prov} value={prov}>
            {prov}
          </option>
        ))}
      </select>

      <label>Cantón</label>
      <select
        name="canton"
        value={shippingInfo.canton}
        onChange={handleShippingInputChange}
        className={styles.formControl}
        required
        disabled={!shippingInfo.provincia} // Disable if no province selected
      >
        <option value="" disabled>Seleccionar una opción</option>
        {shippingInfo.provincia &&
          Object.keys(provinces[shippingInfo.provincia]?.cantones || {}).map((canton) => (
            <option key={canton} value={canton}>
              {canton}
            </option>
          ))}
      </select>

      <label>Distrito</label>
      <select
        name="distrito"
        value={shippingInfo.distrito}
        onChange={handleShippingInputChange}
        className={styles.formControl}
        required
        disabled={!shippingInfo.canton} // Disable if no canton selected
      >
        <option value="" disabled>Seleccionar una opción</option>
        {shippingInfo.canton &&
          provinces[shippingInfo.provincia]?.cantones[shippingInfo.canton]?.map((distrito) => (
            <option key={distrito} value={distrito}>
              {distrito}
            </option>
          ))}
      </select>

      <label>Código Postal (Opcional)</label>
      <input
        type="text"
        name="codigoPostal"
        value={shippingInfo.codigoPostal}
        onChange={handleShippingInputChange}
        placeholder="Código Postal"
        className={styles.formControl}
        pattern={postalCodePattern.source}
      />
    </div>

    {auth.currentUser && (
      <div className={styles.checkboxGroup}>
        <input
          type="checkbox"
          className="form-check-input"
          id="saveDeliveryInfo"
          checked={saveDeliveryInfo}
          onChange={(e) => setSaveDeliveryInfo(e.target.checked)}
        />
        <label htmlFor="saveDeliveryInfo">
          Guardar la información de entrega para futuras compras
        </label>
      </div>
    )}
  </div>
)}


<div className={styles.formGroup}>
  <br></br>
  <h4 className={styles.sectionTitle}>¿Quién Recibe?</h4>
  <div className={styles.checkboxGroup}>
    <input
      type="checkbox"
      className="form-check-input"
      id="sameAsContact"
      checked={isSameAsContact}
      onChange={handleCheckboxChange}
    />
    <label htmlFor="sameAsContact">¿Es el mismo que la información de contacto?</label>
  </div>
  {!isSameAsContact && (
    <>
      <div className={styles.inputContainer}>
        <i className={`fa fa-user ${styles.icon}`}></i>
        <input
          type="text"
          name="name"
          value={quienRecibe.name}
          onChange={handleQuienRecibeChange}
          placeholder="Nombre de quien recibe"
          required
          className={styles.formControl}
        />
      </div>
      <div className={styles.inputContainer}>
        <i className={`fa fa-phone ${styles.icon}`}></i>
        <input
          type="text"
          name="phone"
          value={quienRecibe.phone}
          onChange={handleQuienRecibeChange}
          placeholder="Teléfono de quien recibe"
          required
          className={styles.formControl}
        />
      </div>
    </>
  )}
</div>


    



      <div className={`${styles.formGroup} ${styles.paymentSection}`}>
        <h3 className={styles.sectionTitle}>Método de Pago</h3>
        <div className={styles.radioGroup}>
          <label>
            <input
              type="radio"
              name="paymentMethod"
              value="SINPE Movil"
              checked={paymentMethod === 'SINPE Movil'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            SINPE Movil
          </label>
          <label>
            <input
              type="radio"
              name="paymentMethod"
              value="Transferencia Bancaria"
              checked={paymentMethod === 'Transferencia Bancaria'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            Transferencia Bancaria
          </label>
          <label>
            <input
              type="radio"
              name="paymentMethod"
              value="Pagar en el Local"
              checked={paymentMethod === 'Pagar en el Local'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            Pagar en el Local
          </label>
          <label>
            <input
              type="radio"
              name="paymentMethod"
              value="PayPal"
              checked={paymentMethod === 'PayPal'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            PayPal
          </label>
        </div>

        {paymentMethod === 'SINPE Movil' && (
          <div className={styles.paymentInstructions}>
            <p>
              <strong>Por favor hacer el pago al SINPE  8652 7014 a nombre de Trilogías Decoración S.A.</strong>
            </p>
            <p>En el detalle, por favor incluir el número de orden y tu nombre.</p>
          </div>
        )}

        {paymentMethod === 'Transferencia Bancaria' && (
          <div className={styles.paymentInstructions}>
            <p>
              <strong>Por favor hacer el pago en una de las siguientes cuentas:</strong>
            </p>
            <ul>
              <li>BAC IBAN: CR46010200009193834488 - TRILOGÍAS DECORACIÓN</li>
            </ul>
            <p>En el detalle, por favor incluir el número de orden y tu nombre.</p>
          </div>
        )}

{paymentMethod === 'Pagar en el Local' && (
  <div className={styles.paymentInstructions}>
    <p>
      <strong>Nota Importante:</strong>
    </p>
    <p>
      Para iniciar la preparación de tu pedido, es necesario realizar un 
      <strong> depósito del 50% del total</strong> y enviarnos el comprobante al{' '}
      <strong>
        <a href="https://wa.me/+50664037952" target="_blank" rel="noopener noreferrer">
          WhatsApp: 6403 7952
        </a>
      </strong>.
    </p>
    <p>
      Una vez recibido el depósito, comenzaremos a preparar tu pedido y te notificaremos 
      cuando esté listo para recoger. El saldo restante puede pagarse al recoger el pedido 
      en el local mediante tarjeta de crédito, débito, efectivo, SINPE o transferencia bancaria.
    </p>
    <p>
      Si prefieres pagar el total en el local, ten en cuenta que tu pedido será preparado después 
      del pago completo, y podrías esperar el tiempo de preparación en nuestra tienda.
    </p>
  </div>
)}



{paymentMethod === 'PayPal' && (
  <div className={styles.paymentInstructions}>
    <p>
      <strong>
        Por favor envía el pago a la cuenta de PayPal: info@armonyfloristeria.com
      </strong>
    </p>
    <p>
      En el detalle, incluye tu número de orden y tu nombre, y envíanos el comprobante al{' '}
      <strong>
        <a href="https://wa.me/+50664037952" target="_blank" rel="noopener noreferrer">
          WhatsApp: 6403 7952
        </a>
      </strong>.
    </p>

                <p className={styles.feeNote}>
                  <strong>Nota:</strong> la comisión fija de PayPal de <strong>US$3</strong> se convierte
                  automáticamente a colones con el tipo de cambio del <strong>BCCR</strong> y se suma al
                  total de tu compra en el “Resumen de tu Orden”.
                  {fxLoading ? (
                    ' (calculando tasa...)'
                  ) : fxError ? (
                    <span className={styles.errorText}> — {fxError}</span>
                  ) : (
                    <>
                      {' '}<br></br>Monto estimado hoy: <strong>{formatCurrency(paypalFeeCRC)}</strong>.
                    </>
                  )}
                </p>
              </div>
            )}

  </div>

    {/* Código de descuento */}
      <div className={`${styles.formGroup} ${styles.discountSection}`}>
        <h3 className={styles.sectionTitle}>Código de Descuento</h3>
        <div className={styles.discountInputRow}>
          <div className={styles.inputContainer}>
            <i className={`fa fa-tag ${styles.icon}`}></i>
            <input
              type="text"
              value={discountCode}
              onChange={handleDiscountCodeChange}
              placeholder="Opcional - Ingresa tu código"
              className={`${styles.formControl} ${styles.discountInput}`}
            />
          </div>
          <button
            type="button"
            onClick={handleApplyDiscount}
            className={styles.discountApplyBtn}
            disabled={isCheckingDiscount || !discountCode.trim()}
          >
            {isCheckingDiscount ? 'Validando...' : 'Aplicar'}
          </button>
        </div>
        {discountMessage && (
          <p
            className={`${styles.discountMessage} ${
              discountData
                ? styles.discountMessageSuccess
                : styles.discountMessageError
            }`}
          >
            {discountMessage}
          </p>
        )}
      </div>



      <div className={`${styles.formGroup} ${styles.orderSummarySection}`}>
        <h3 className={styles.sectionTitle}>Resumen de tu Orden</h3>
              <ul className={styles.orderSummaryList}>
          {cart.map((item, index) => (
            <li key={index} className={styles.orderItem}>
              <img
                src={getOrderThumb(item)}
                alt={item.name}
                className={styles.orderItemImage}
                loading="lazy"
                decoding="async"
              />
              <div className={styles.orderItemDetails}>
                <p>{item.name}</p>
                <p>{formatCurrency(item.price)} x {item.quantity}</p>

                {item.option && (
                  <p>Opción: {item.option.name}</p>
                )}

                {item.tarjetaMessage && (
                  <p>Mensaje en la tarjeta: {item.tarjetaMessage}</p>
                )}
              </div>
            </li>
          ))}
        </ul>



        {shippingMethod === 'delivery' && (
          <h4 className={styles.totalTitle}>
            Envío: {formatCurrency(deliveryPrice)}
          </h4>
        )}

        {discountAmount > 0 && (
          <>
            <div className={styles.discountSummaryLine}>
              <span className={styles.discountSummaryLabel}>Descuento aplicado:</span>
              <span className={styles.discountTag}>
                {discountData?.code || ''}
              </span>
            </div>
            <h4 className={`${styles.totalTitle} ${styles.discountLine}`}>
              Descuento: -{formatCurrency(discountAmount)}
            </h4>
          </>
        )}

        {paymentMethod === 'PayPal' && (
          <h4 className={styles.totalTitle}>
            Comisión PayPal (US$3): {formatCurrency(paypalFeeCRC)}
            {fxLoading && ' (calculando...)'}
            {fxError && <span className={styles.errorText}> — {fxError}</span>}
          </h4>
        )}





        <h4 className={styles.totalTitle}>
          Total: {formatCurrency(orderTotal)}
        </h4>

      </div>

      <button onClick={handleCheckout} className={styles.checkoutBtn} disabled={isProcessing}>
        {isProcessing ? (
          <>
            <span className={styles.spinnerSmall}></span> Procesando...
          </>
        ) : (
          'Proceder al Pago'
        )}
      </button>

      {isProcessing && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {isPaymentProcessed && (
        <Modal
          isOpen={isPaymentProcessed}
          onRequestClose={handleCloseModal}
          contentLabel="Confirmación de Orden"
          className={styles.modalContent}
          overlayClassName={styles.modalOverlay}
        >
          <h2>Pedido Realizado con Éxito!</h2>
          {orderID && (
            <>
              <p>Este es tu número de Orden:</p>
              <p>
                <strong>{orderID}</strong>
              </p>
            </>
          )}
          <p>Recuerda por favor incluirlo en el detalle del pago junto a tu nombre.</p>
          <button onClick={handleCloseModal} className="btn btn-primary">
            Cerrar
          </button>
        </Modal>
      )}
    </div>
  );
};

export default Checkout;
